import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TypographyState, FontFamily, TypographyScale, TypographyConfig } from './typography.interface';

@Injectable({
  providedIn: 'root'
})
export class TypographyStore {
  private stateSubject = new BehaviorSubject<TypographyState>({
    fontFamilies: [],
    typographyScales: [],
    activeTypographyConfig: null,
    isLoading: false,
    lastUpdated: null,
    error: null
  });

  /**
   * Get current typography state
   */
  getCurrentState(): TypographyState {
    return this.stateSubject.value;
  }

  /**
   * Get typography state as observable
   */
  getState$(): Observable<TypographyState> {
    return this.stateSubject.asObservable();
  }

  /**
   * Get font families observable
   */
  getFontFamilies$(): Observable<FontFamily[]> {
    return this.stateSubject.pipe(
      map(state => state.fontFamilies)
    );
  }

  /**
   * Get typography scales observable
   */
  getTypographyScales$(): Observable<TypographyScale[]> {
    return this.stateSubject.pipe(
      map(state => state.typographyScales)
    );
  }

  /**
   * Get active typography config observable
   */
  getActiveTypographyConfig$(): Observable<TypographyConfig | null> {
    return this.stateSubject.pipe(
      map(state => state.activeTypographyConfig)
    );
  }

  /**
   * Get loading state observable
   */
  getLoadingState$(): Observable<boolean> {
    return this.stateSubject.pipe(
      map(state => state.isLoading)
    );
  }

  /**
   * Get error state observable
   */
  getErrorState$(): Observable<string | null> {
    return this.stateSubject.pipe(
      map(state => state.error)
    );
  }

  /**
   * Get current font families
   */
  getCurrentFontFamilies(): FontFamily[] {
    return this.stateSubject.value.fontFamilies;
  }

  /**
   * Get current typography scales
   */
  getCurrentTypographyScales(): TypographyScale[] {
    return this.stateSubject.value.typographyScales;
  }

  /**
   * Get current active typography config
   */
  getCurrentActiveTypographyConfig(): TypographyConfig | null {
    return this.stateSubject.value.activeTypographyConfig;
  }

  /**
   * Update font families
   */
  updateFontFamilies(fontFamilies: FontFamily[]): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({
      ...currentState,
      fontFamilies,
      lastUpdated: new Date(),
      error: null
    });

    console.log('🎨 TypographyStore: Font families updated', fontFamilies);
  }

  /**
   * Update typography scales
   */
  updateTypographyScales(typographyScales: TypographyScale[]): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({
      ...currentState,
      typographyScales,
      lastUpdated: new Date(),
      error: null
    });

    console.log('🎨 TypographyStore: Typography scales updated', typographyScales);
  }

  /**
   * Update active typography configuration
   */
  updateActiveTypographyConfig(config: TypographyConfig | null): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({
      ...currentState,
      activeTypographyConfig: config,
      lastUpdated: new Date(),
      error: null
    });

    console.log('🎨 TypographyStore: Active typography config updated', config);
  }

  /**
   * Update complete typography state
   */
  updateTypographyState(partialState: Partial<TypographyState>): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({
      ...currentState,
      ...partialState,
      lastUpdated: new Date(),
      error: null
    });

    console.log('🎨 TypographyStore: Typography state updated', partialState);
  }

  /**
   * Set loading state
   */
  setLoading(isLoading: boolean): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({
      ...currentState,
      isLoading,
      error: isLoading ? null : currentState.error
    });

    console.log('🎨 TypographyStore: Loading state set to', isLoading);
  }

  /**
   * Set error state
   */
  setError(error: string | null): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({
      ...currentState,
      error,
      isLoading: false
    });

    console.log('🎨 TypographyStore: Error state set to', error);
  }

  /**
   * Clear error state
   */
  clearError(): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({
      ...currentState,
      error: null
    });

    console.log('🎨 TypographyStore: Error state cleared');
  }

  /**
   * Reset typography store to initial state
   */
  reset(): void {
    this.stateSubject.next({
      fontFamilies: [],
      typographyScales: [],
      activeTypographyConfig: null,
      isLoading: false,
      lastUpdated: null,
      error: null
    });

    console.log('🎨 TypographyStore: Store reset to initial state');
  }

  /**
   * Check if typography data is loaded
   */
  isTypographyDataLoaded(): boolean {
    const state = this.stateSubject.value;
    return state.fontFamilies.length > 0 && 
           state.typographyScales.length > 0 && 
           state.activeTypographyConfig !== null;
  }

  /**
   * Get default typography scale
   */
  getDefaultTypographyScale(): TypographyScale | null {
    const scales = this.stateSubject.value.typographyScales;
    return scales.find(scale => scale.is_default) || scales[0] || null;
  }

  /**
   * Get typography scale by name
   */
  getTypographyScaleByName(name: 'small' | 'default' | 'large'): TypographyScale | null {
    const scales = this.stateSubject.value.typographyScales;
    return scales.find(scale => scale.name === name) || null;
  }

  /**
   * Get active font family
   */
  getActiveFontFamily(): FontFamily | null {
    const config = this.stateSubject.value.activeTypographyConfig;
    return config?.font_family || null;
  }

  /**
   * Get active typography scale
   */
  getActiveTypographyScale(): TypographyScale | null {
    const config = this.stateSubject.value.activeTypographyConfig;
    return config?.typography_scale || null;
  }
}

// Import map operator
import { map } from 'rxjs/operators';
